import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { Vec3 } from '../../geo/types';
import { HULL_STATIONS, type ShipTemplate } from './shipTemplates';
import { hullStations, stationAt, type HullStation } from './hull';
import { loftSolid, sparSolid, type Ring3 } from './shipSolids';
import { floorHeight, innerStarboard } from './boatSection';

/*
 * The fittings of an open boat (docs/plans/ships.md 9). Thwarts: the woodcut seats its rowers one
 * behind the other over about 0.4 of the boat's length (woodcut 3140,3260-3330,3380), a thwart to
 * each rowing room of about a metre (the room of excavated North European oared craft, 0.9-1.0 m),
 * level, 0.2 m under the gunwale amidships. These are rowing-boat fittings, not a seating plan
 * for every open lighter. Oars: the woodcut's reach 0.5 of the boat's length
 * outboard, 0.6-0.7 overall; a boat at rest has them shipped, laid fore and aft on its thwarts,
 * as long as its hold takes them level. Frames: the painting crosses each open lighter with some
 * 12 transverse strokes (scan 3570,2740-3740,2830), about 0.6 m apart on an 8 m boat.
 */

export const THWART = { room: 1.0, span: 0.4, drop: 0.2, thickness: 0.05, width: 0.22 } as const;
export const OAR = {
  share: 0.65,
  radius: 0.035,
  sides: 6,
  bladeShare: 0.25,
  bladeWidth: 0.13,
  bladeThickness: 0.02,
  /** How far off the centre line an oar lies, of the hold's half-width at the thwarts. */
  offset: 0.45,
  /** Room left between an oar and the floor where the bottom rises towards stem and stern. */
  clearance: 0.03,
} as const;
export const FRAME_SPACING_M = 0.6;
const FRAME = { width: 0.06, intoShell: 0.02, proud: 0.03, belowGunwale: 0.04 } as const;
/* A thwart's ends run this far into the shell, so no gap opens between the stations. */
const THWART_BEDDING = 0.02;
const LOCAL: Frame = { origin: [0, 0, 0], yaw: 0 };
const REACH_STEP_M = 0.05;
const LIGHTER_BEAM = { at: 0.28, width: 0.13, thickness: 0.065 } as const;
const FLOOR_RIB = { width: 0.06, rise: 0.07, bedding: 0.012 } as const;
const KEELSON = { width: 0.1, rise: 0.055, bedding: 0.02 } as const;

export interface Oar {
  readonly from: Vec3;
  readonly to: Vec3;
  readonly radius: number;
}

const stationOf = (template: ShipTemplate, x: number): HullStation =>
  stationAt(hullStations(template), 0.5 + x / template.length);

/** The top of every thwart: level, the drop under the gunwale amidships. */
export function thwartTop(template: ShipTemplate): number {
  return stationOf(template, 0).gunwale - THWART.drop;
}

/** Where the thwarts stand, metres from amidships, a rowing room apart and centred. */
export function thwartStations(template: ShipTemplate): readonly number[] {
  const count = Math.floor((THWART.span * template.length) / THWART.room) + 1;
  return Array.from({ length: count }, (_, i) => (i - (count - 1) / 2) * THWART.room);
}

/** The half-width of the hold at height `y` at a station, inside the shell. */
export function holdHalfWidth(st: HullStation, y: number): number {
  const inside = innerStarboard(st);
  for (let i = 0; i + 1 < inside.length; i++) {
    const [a, b] = [inside[i]!, inside[i + 1]!];
    if (y <= a[1] && y >= b[1]) return b[2] + ((y - b[1]) / (a[1] - b[1])) * (a[2] - b[2]);
  }
  return y > inside[0]![1] ? inside[0]![2] : 0;
}

export function buildThwarts(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  const top = thwartTop(template);
  for (const x of thwartStations(template)) {
    const reach = holdHalfWidth(stationOf(template, x), top - THWART.thickness) + THWART_BEDDING;
    const half = THWART.width / 2;
    builder.box(style, LOCAL, [x - half, top - THWART.thickness, -reach], [x + half, top, reach]);
  }
}

/** The long painted lighters retain open central holds. Two narrow end ties are an estimate;
 * the transverse strokes throughout their holds are represented separately as ribs. */
export function lighterBeamStations(template: ShipTemplate): readonly number[] {
  return [-LIGHTER_BEAM.at * template.length, LIGHTER_BEAM.at * template.length];
}

export function buildLighterBeams(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  for (const x of lighterBeamStations(template)) {
    const st = stationOf(template, x);
    const top = st.gunwale - THWART.drop;
    const reach = holdHalfWidth(st, top - LIGHTER_BEAM.thickness) + THWART_BEDDING;
    const half = LIGHTER_BEAM.width / 2;
    builder.box(
      style,
      LOCAL,
      [x - half, top - LIGHTER_BEAM.thickness, -reach],
      [x + half, top, reach]
    );
  }
}

/* The longest oar, up to the woodcut's, that lies level on the thwarts clear of the rising floor. */
function oarLength(template: ShipTemplate, bottom: number): number {
  const most = OAR.share * template.length;
  let half = 0;
  while (half + REACH_STEP_M <= most / 2) {
    const next = half + REACH_STEP_M;
    const ends = [stationOf(template, -next), stationOf(template, next)];
    if (ends.some((st) => floorHeight(st) + OAR.clearance > bottom)) break;
    half = next;
  }
  return 2 * half;
}

/** A pair of oars shipped fore and aft on the thwarts, one to each side of the centre line. */
export function oarsOf(template: ShipTemplate): readonly Oar[] {
  const bottom = thwartTop(template);
  const length = oarLength(template, bottom);
  const y = bottom + OAR.radius;
  const z = OAR.offset * holdHalfWidth(stationOf(template, 0), bottom);
  return [-z, z].map((side) => ({
    from: [-length / 2, y, side],
    to: [length / 2, y, side],
    radius: OAR.radius,
  }));
}

/* Each oar's loom, its blade aft, flat on the thwarts. */
export function buildOars(builder: MeshBuilder, template: ShipTemplate, style: VertexStyle): void {
  for (const oar of oarsOf(template)) {
    const blade = OAR.bladeShare * (oar.to[0] - oar.from[0]);
    sparSolid(builder, style, {
      from: oar.from,
      to: [oar.to[0] - blade, oar.to[1], oar.to[2]],
      radiusFrom: oar.radius,
      radiusTo: oar.radius,
      sides: OAR.sides,
      painterWidthScale: 1,
    });
    const [halfW, halfT] = [OAR.bladeWidth / 2, OAR.bladeThickness / 2];
    const [x1, y, z] = oar.to;
    builder.box(
      style,
      LOCAL,
      [x1 - blade - oar.radius, y - halfT, z - halfW],
      [x1, y + halfT, z + halfW]
    );
  }
}

/** Where the frames stand, metres from amidships, the painted spacing apart, inside the first
    and last stations, where the hold rises to the posts. */
export function frameStations(template: ShipTemplate): readonly number[] {
  const clear = template.length / 2 - template.length / HULL_STATIONS;
  const each = Math.floor(clear / FRAME_SPACING_M - 1e-9);
  return Array.from({ length: 2 * each + 1 }, (_, i) => (i - each) * FRAME_SPACING_M);
}

/* The inside of the shell across a station, starboard gunwale to port gunwale, its ends brought
   down under the gunwale cap. */
function framePath(st: HullStation): readonly Vec3[] {
  const starboard = innerStarboard(st);
  const port = [...starboard].reverse().map(([x, y, z]): Vec3 => [x, y, -z]);
  const path = [...starboard, ...port.slice(starboard.at(-1)![2] === 0 ? 1 : 0)];
  const lowered = (p: Vec3): Vec3 => [p[0], p[1] - FRAME.belowGunwale, p[2]];
  return [lowered(path[0]!), ...path.slice(1, -1), lowered(path.at(-1)!)];
}

/* The unit normal into the hold at path point i: the path's direction turned a quarter towards
   the middle, averaged over the segments meeting there. */
function inwardAt(path: readonly Vec3[], i: number): readonly [number, number] {
  const a = path[Math.max(i - 1, 0)]!;
  const b = path[Math.min(i + 1, path.length - 1)]!;
  const [ty, tz] = [b[1] - a[1], b[2] - a[2]];
  const n = Math.hypot(ty, tz);
  return [-tz / n, ty / n];
}

function frameRing(path: readonly Vec3[], i: number): Ring3 {
  const [x, y, z] = path[i]!;
  const [ny, nz] = inwardAt(path, i);
  const at = (dx: number, d: number): Vec3 => [x + dx, y + d * ny, z + d * nz];
  const [w, lo, hi] = [FRAME.width / 2, -FRAME.intoShell, FRAME.proud];
  return [at(-w, lo), at(w, lo), at(w, hi), at(-w, hi)];
}

export function buildFrames(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  for (const x of frameStations(template)) {
    const path = framePath(stationOf(template, x));
    loftSolid(
      builder,
      style,
      path.map((_, i) => frameRing(path, i))
    );
  }
}

/** Low closed rib strips keep the painting's repeated transverse strokes visible at distance,
 * without putting rowing seats across a lighter's hold. Their triangular section is a distance
 * simplification; the near tier builds the full frames up both sides. */
export function buildFloorRibs(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  for (const x of frameStations(template)) {
    const st = stationOf(template, x);
    const floor = floorHeight(st);
    const reach = holdHalfWidth(st, floor + FLOOR_RIB.bedding);
    const half = FLOOR_RIB.width / 2;
    const ring = (z: number): Ring3 => [
      [x - half, floor + FLOOR_RIB.bedding, z],
      [x, floor + FLOOR_RIB.rise, z],
      [x + half, floor + FLOOR_RIB.bedding, z],
    ];
    loftSolid(builder, style, [ring(-reach), ring(reach)]);
  }
}

/** A small central timber follows the lighter's rising floor. Its concealed construction and
 * dimensions are estimated for close viewing, not identified in the painting. */
export function buildLighterKeelson(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  const half = KEELSON.width / 2;
  const rings = hullStations(template)
    .slice(1, -1)
    .map((st): Ring3 => {
      const floor = floorHeight(st);
      return [
        [st.x, floor - KEELSON.bedding, -half],
        [st.x, floor - KEELSON.bedding, half],
        [st.x, floor + KEELSON.rise, half],
        [st.x, floor + KEELSON.rise, -half],
      ];
    });
  loftSolid(builder, style, rings);
}

/** Short timber mooring cleats at opposite ends and knees bedding the end thwarts into the
 * sides. Small working fittings are inferred for the close view; the painting cannot resolve
 * their exact shape or placement. Every piece remains inside the hull's reserved beam. */
export function buildBoatJoinery(
  builder: MeshBuilder,
  template: ShipTemplate,
  style: VertexStyle
): void {
  for (const sign of [-1, 1]) {
    const x = sign * template.length * 0.3;
    const st = stationOf(template, x);
    const z = sign * st.halfBreadth * 0.8;
    const y = st.gunwale;
    builder.box(style, LOCAL, [x - 0.065, y - 0.02, z - 0.065], [x + 0.065, y + 0.14, z + 0.065]);
    builder.box(style, LOCAL, [x - 0.19, y + 0.1, z - 0.075], [x + 0.19, y + 0.18, z + 0.075]);
  }
  const ends = template.type === 'praam' ? lighterBeamStations(template) : thwartStations(template);
  for (const x of [ends[0]!, ends.at(-1)!]) {
    const st = stationOf(template, x);
    const top = template.type === 'praam' ? st.gunwale - THWART.drop : thwartTop(template);
    for (const sign of [-1, 1]) {
      const z = sign * st.halfBreadth * 0.75;
      builder.box(style, LOCAL, [x - 0.07, top - 0.16, z - 0.07], [x + 0.07, top + 0.01, z + 0.07]);
    }
  }
}
